import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { taskFiltersFromState } from "../lib/dashboard";
import useDirectory from "../hooks/useDirectory";
import useTaskActions from "../hooks/useTaskActions";
import useVisiblePolling from "../hooks/useVisiblePolling";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import {
  TASK_TYPES, TASK_TYPE_LABELS, TASK_DOMAINS, TASK_DOMAIN_LABELS, PRIORITY_LEVELS, PRIORITY_LABELS, taskMatchesStatusFilter, isTaskOverdue, DUE_DATE_FILTERS, taskMatchesFilters, isMyOpenTask, isTaskThisWeek, isTaskCritical,
} from "../lib/tasks";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ResponsivePopover as Popover, ResponsivePopoverTrigger as PopoverTrigger, ResponsivePopoverContent as PopoverContent } from "../components/ui/responsive-popover";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuTrigger as DropdownMenuTrigger, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
} from "../components/ui/responsive-dropdown-menu";
import { toast } from "sonner";
import { Plus, ChevronDown, CalendarDays, Search as SearchIcon, SlidersHorizontal, X } from "lucide-react";
import TaskFormSheet, { ProspectFilter } from "../components/TaskFormSheet";
import TaskTimeline from "../components/TaskTimeline";
import CancelTaskDialog from "../components/CancelTaskDialog";
import { KanbanView } from "../components/tasks/TaskKanbanView";
import { KANBAN_SORTS, groupTasksByColumn, moveRequirement, taskColumnId } from "../lib/kanban";
import CompleteTaskDialog from "../components/CompleteTaskDialog";
import { TaskDetailSheet, DeleteTaskDialog } from "../components/TaskDetailSheet";
import { ListView, EmptyState } from "../components/tasks/TaskListView";
import { OperationalViewsRow, ViewToggle } from "../components/tasks/TaskToolbar";
import { FilterField } from "../components/FilterField";

const PAGE_SIZE = 8;
const STATUS_TABS = [
  { id: "all", label: "Toutes" },
  { id: "a_faire", label: "À faire" },
  { id: "en_cours", label: "En cours" },
  { id: "en_retard", label: "En retard" },
  { id: "termine", label: "Terminées" },
  { id: "annulee", label: "Annulées" },
];

export default function Tasks() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Reflected in the URL (?view=list|timeline) rather than component state alone so
  // a shared/bookmarked link keeps the chosen view. "kanban" is the implicit
  // default and never appears in the URL.
  const requestedView = searchParams.get("view");
  const view = requestedView === "timeline" || requestedView === "list" ? requestedView : "kanban";
  const setView = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "timeline" || next === "list") params.set("view", next); else params.delete("view");
    setSearchParams(params, { replace: true });
  };
  const { user } = useAuth();
  const canCreate = hasPermission(user, "tasks:create");
  const canEdit = hasPermission(user, "tasks:edit");
  const canDelete = hasPermission(user, "tasks:delete");
  const canScopeAll = hasPermission(user, "tasks:scope_all");
  const directory = useDirectory(canScopeAll || canEdit);
  const canViewProspects = hasPermission(user, "prospects:view");

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prospects, setProspects] = useState([]);

  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  // Arriving on the page shows one's own tasks first (the « Mes tâches » view); the filter can be lifted at any time.
  const [responsableFilter, setResponsableFilter] = useState("moi");
  const [prospectFilter, setProspectFilter] = useState(null);
  const [domainFilter, setDomainFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [dueDateFilter, setDueDateFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [kanbanSort, setKanbanSort] = useState("due");
  // Kanban only: where a card is SHOWN while its move is saved or waits for a dialog (optimistic move), the dropped task's
  // flash when a move is abandoned, and the column a "+" quick add will move the new task into.
  const [moveOverrides, setMoveOverrides] = useState({});
  const [dialogMove, setDialogMove] = useState(null);
  const [revertedId, setRevertedId] = useState(null);
  const [createStatus, setCreateStatus] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [createType, setCreateType] = useState(null);
  const [viewingTask, setViewingTask] = useState(null);

  // `silent`: a background refresh (a colleague changed something) never blanks the list and never toasts an error.
  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/tasks");
      setTasks(data);
      // An open detail follows the refreshed data (a colleague moved the status); a task deleted meanwhile closes it.
      setViewingTask((prev) => (prev ? data.find((t) => t.id === prev.id) || null : prev));
    } catch (e) {
      if (!silent) toast.error("Impossible de charger les tâches. Réessayez dans quelques instants.");
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  // Colleagues edit the same tasks: re-read every 15 s while the tab is visible (and once when it comes back), except while the
  // person is in the middle of a form or a drag whose result a refresh could scramble.
  const busy = formOpen || !!dialogMove || Object.keys(moveOverrides).length > 0;
  useVisiblePolling(() => { if (!busy) load({ silent: true }); }, 15000);


  useEffect(() => {
    // GET /tasks never resolves prospect_id -> pharmacy_name, so GET /prospects
    // is reused as a client-side id->name lookup. Gated on prospects:view
    // (independent from tasks:view); without it, or for a prospect outside the
    // caller's scope, the name stays unresolved and a generic "Prospect lié"
    // link is shown, never a leak.
    if (!canViewProspects) return;
    api.get("/prospects").then(({ data }) => setProspects(data)).catch(() => {});
  }, [canViewProspects]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, typeFilter, responsableFilter, prospectFilter, domainFilter, priorityFilter, dueDateFilter, search]);

  // Full reset (incl. status), for the empty state's "start over" action.
  const resetFilters = () => {
    setStatusFilter("all"); setTypeFilter("all"); setResponsableFilter("all"); setProspectFilter(null);
    setDomainFilter("all"); setPriorityFilter("all"); setDueDateFilter("all"); setSearch("");
  };
  const hasActiveFilters = statusFilter !== "all" || typeFilter !== "all" || responsableFilter !== "all" || !!prospectFilter
    || domainFilter !== "all" || priorityFilter !== "all" || dueDateFilter !== "all" || !!search;

  // Status tabs are the workflow, not a filter: the compact bar's
  // Réinitialiser/chips/badge below never touch statusFilter, only the 7
  // actual filter dimensions.
  const resetAdvancedFilters = () => {
    setTypeFilter("all"); setResponsableFilter("all"); setProspectFilter(null);
    setDomainFilter("all"); setPriorityFilter("all"); setDueDateFilter("all"); setSearch("");
  };
  // Only the four fields inside the "Filtres" popover: the count shown on its
  // trigger button ("Filtres 2").
  const secondaryFilterCount = [
    typeFilter !== "all",
    canScopeAll && responsableFilter !== "all",
    !!prospectFilter,
    dueDateFilter !== "all",
  ].filter(Boolean).length;

  const directoryName = (id) => directory.find((u) => u.id === id)?.name || (id === user?.id ? user.name : null);
  const resolveAssignee = (id) => {
    if (id === user?.id) return { name: user.name, avatar_url: user.avatar_url };
    const found = directory.find((u) => u.id === id);
    return found ? { name: found.name, avatar_url: found.avatar_url } : null;
  };
  const resolveProspectName = (id) => prospects.find((p) => p.id === id)?.pharmacy_name || null;
  const directoryNames = (ids) => {
    const names = (ids || []).map(directoryName).filter(Boolean);
    return names.length ? names.join(", ") : null;
  };

  // Every active filter (all 7, including the always-visible search/domain/
  // priority) as a removable chip. Status is excluded: the tabs show it.
  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (search) chips.push({ id: "search", label: `Recherche : ${search}`, onRemove: () => setSearch("") });
    if (domainFilter !== "all") {
      chips.push({ id: "domain", label: `Domaine : ${TASK_DOMAIN_LABELS[domainFilter]}`, onRemove: () => setDomainFilter("all") });
    }
    if (priorityFilter !== "all") {
      chips.push({ id: "priority", label: `Priorité : ${PRIORITY_LABELS[priorityFilter]}`, onRemove: () => setPriorityFilter("all") });
    }
    if (typeFilter !== "all") {
      chips.push({ id: "type", label: `Type : ${TASK_TYPE_LABELS[typeFilter]}`, onRemove: () => setTypeFilter("all") });
    }
    if (canScopeAll && responsableFilter !== "all") {
      const label = responsableFilter === "moi" ? "Moi" : (directoryName(responsableFilter) || "…");
      chips.push({ id: "responsable", label: `Responsable : ${label}`, onRemove: () => setResponsableFilter("all") });
    }
    if (prospectFilter) {
      chips.push({ id: "prospect", label: `Prospect : ${prospectFilter.pharmacy_name}`, onRemove: () => setProspectFilter(null) });
    }
    if (dueDateFilter !== "all") {
      const label = DUE_DATE_FILTERS.find((d) => d.id === dueDateFilter)?.label || dueDateFilter;
      chips.push({ id: "due", label: `Échéance : ${label}`, onRemove: () => setDueDateFilter("all") });
    }
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, domainFilter, priorityFilter, typeFilter, responsableFilter, canScopeAll, prospectFilter, dueDateFilter, directory, user]);

  // Every count is derived from `tasks`, the single GET /tasks result, already
  // scope-limited server-side (_scope_query_for) for a caller without
  // tasks:scope_all, so none of these can reveal a task outside that scope.
  const counts = useMemo(() => {
    const aFaire = tasks.filter((t) => taskMatchesStatusFilter(t, "a_faire")).length;
    const enCours = tasks.filter((t) => taskMatchesStatusFilter(t, "en_cours")).length;
    const enRetard = tasks.filter(isTaskOverdue).length;
    const termine = tasks.filter((t) => t.status === "termine").length;
    const annulee = tasks.filter((t) => t.status === "annulee").length;
    const mine = tasks.filter((t) => isMyOpenTask(t, user?.id)).length;
    const thisWeek = tasks.filter((t) => isTaskThisWeek(t)).length;
    const critical = tasks.filter(isTaskCritical).length;
    return { aFaire, enCours, enRetard, termine, annulee, mine, thisWeek, critical, all: tasks.length };
  }, [tasks, user]);

  const filtered = useMemo(() => {
    return tasks.filter((t) => taskMatchesFilters(t, {
      statusFilter, typeFilter, responsableFilter,
      prospectId: prospectFilter?.id || null,
      domainFilter, priorityFilter, dueDateFilter, search,
    }, user?.id)).sort((a, b) => {
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(a.due_date) - new Date(b.due_date);
    });
  }, [tasks, statusFilter, typeFilter, responsableFilter, prospectFilter, domainFilter, priorityFilter, dueDateFilter, search, user]);

  // Kanban: the columns ARE the statuses, so the status tabs are hidden there; only the cross-cutting "En retard" shortcut still
  // applies. Every other filter is shared with the list and the timeline.
  const kanbanTasks = useMemo(() => tasks.filter((t) => taskMatchesFilters(t, {
    statusFilter: statusFilter === "en_retard" ? "en_retard" : "all", typeFilter, responsableFilter,
    prospectId: prospectFilter?.id || null,
    domainFilter, priorityFilter, dueDateFilter, search,
  }, user?.id)), [tasks, statusFilter, typeFilter, responsableFilter, prospectFilter, domainFilter, priorityFilter, dueDateFilter, search, user]);
  const kanbanGroups = useMemo(
    () => groupTasksByColumn(kanbanTasks, kanbanSort, dialogMove ? { ...moveOverrides, [dialogMove.id]: dialogMove.target } : moveOverrides),
    [kanbanTasks, kanbanSort, moveOverrides, dialogMove],
  );
  const shownCount = view === "kanban" ? kanbanTasks.length : filtered.length;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const openCreate = (type, status = null) => { setEditingTask(null); setCreateType(type || null); setCreateStatus(status); setFormOpen(true); };
  const openEdit = (task) => { setViewingTask(null); setEditingTask(task); setFormOpen(true); };

  // Header "+" quick action lands here with { openCreate: true } in nav
  // state to open the drawer immediately instead of just showing the list.
  // Cleared via replace so back/refresh doesn't reopen it.
  useEffect(() => {
    if (!location.state?.openCreate || !canCreate) return;
    openCreate();
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  // The tableau de bord lands here with { filters } (lib/dashboard.js: only values this list knows), applied once then cleared.
  useEffect(() => {
    const preset = taskFiltersFromState(location.state);
    if (!preset) return;
    setStatusFilter("all"); setTypeFilter(preset.type || "all"); setPriorityFilter(preset.priority || "all");
    setDueDateFilter(preset.due || "all"); setDomainFilter("all"); setProspectFilter(null); setSearch("");
    setResponsableFilter(preset.responsable || "all");
    navigate(location.pathname + location.search, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  const openView = (task) => setViewingTask(task);
  // Global search lands here with { openTask: id }: open that task's drawer once the list is loaded (state cleared like openCreate).
  useEffect(() => {
    const wanted = location.state?.openTask;
    if (!wanted || !tasks.length) return;
    const found = tasks.find((t) => t.id === wanted);
    if (found) setViewingTask(found);
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, tasks]);

  // A link from the Copilote (`/tasks?task=<id>`) opens that task's drawer once the list is loaded, then drops the parameter.
  useEffect(() => {
    const wanted = searchParams.get("task");
    if (!wanted || !tasks.length) return;
    const found = tasks.find((t) => t.id === wanted);
    if (found) setViewingTask(found);
    const params = new URLSearchParams(searchParams);
    params.delete("task");
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, tasks]);

  // Single place that keeps the list and an open detail drawer (if it's the
  // same task) in sync after any patch, so the drawer never shows stale
  // status/due_date.
  const updateTaskInState = (updated) => {
    setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    setViewingTask((prev) => (prev && prev.id === updated.id ? updated : prev));
  };

  const {
    completing, setCompleting, deletingTask, setDeletingTask, cancelling, setCancelling,
    markDone, cycleStatus, toggleDone, confirmComplete, changeStatus, confirmCancel, postpone, addAssignee, confirmDelete,
  } = useTaskActions({
    onUpdated: updateTaskInState,
    onDeleted: (task) => {
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      setViewingTask((prev) => (prev && prev.id === task.id ? null : prev));
    },
    onFormCompleted: () => { setFormOpen(false); setEditingTask(null); },
  });

  const onSaved = (task, isNew) => {
    setTasks((prev) => (isNew ? [task, ...prev] : prev.map((t) => (t.id === task.id ? task : t))));
    setViewingTask((prev) => (prev && prev.id === task.id ? task : prev));
    setFormOpen(false);
    // "+" in the « En cours » column: the API creates every task « À faire », so the new card is moved right after.
    if (isNew && createStatus && createStatus !== "a_faire") moveTask(task, createStatus);
    setCreateStatus(null);
  };

  // ── Kanban moves ───────────────────────────────────────────────────────────
  // One handler for the drop and for each card's « Déplacer vers » entry. The card is shown in its target column at once
  // (`moveOverrides` / `dialogMove`) so it never snaps back while a request or a dialog is pending; a failed or abandoned
  // move puts it back with the `.card-reverted` flash, like the Pipeline.
  const flashReverted = (id) => {
    setRevertedId(id);
    setTimeout(() => setRevertedId((current) => (current === id ? null : current)), 600);
  };
  const moveTask = async (task, target) => {
    const requirement = moveRequirement(taskColumnId(task), target);
    if (requirement === "none") return;
    if (requirement === "complete") {
      setDialogMove({ id: task.id, target });
      markDone(task);
      return;
    }
    if (requirement === "cancel") {
      setDialogMove({ id: task.id, target });
      setCancelling({ task });
      return;
    }
    setMoveOverrides((prev) => ({ ...prev, [task.id]: target }));
    const done = await changeStatus(task, target);
    setMoveOverrides((prev) => { const next = { ...prev }; delete next[task.id]; return next; });
    if (!done) flashReverted(task.id);
  };
  // Both dialogs closed: the pending move is over, whether confirmed (the task already carries its new status) or abandoned.
  useEffect(() => {
    if (!dialogMove || completing || cancelling) return;
    const task = tasks.find((t) => t.id === dialogMove.id);
    if (task && taskColumnId(task) !== dialogMove.target) flashReverted(dialogMove.id);
    setDialogMove(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completing, cancelling]);

  const todayMarkerRef = useRef(null);
  const scrollToToday = () => todayMarkerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <div className="space-y-3" data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-900">Tâches</h1>
          <p className="hidden md:block text-sm text-slate-500">Organisez et suivez toutes vos actions pour ne rien laisser passer.</p>
          <p className="md:hidden text-sm text-slate-500">{shownCount} tâche{shownCount === 1 ? "" : "s"}</p>
        </div>
        <div className={`${view === "timeline" ? "flex" : "hidden md:flex"} self-start items-center gap-2`}>
          {view === "timeline" && (
            <Button
              variant="outline" size="sm" className="h-9 gap-1.5" data-testid="btn-scroll-today"
              onClick={scrollToToday}
            >
              <CalendarDays size={14} /> Aujourd'hui
            </Button>
          )}
          {/* The in-page create button is desktop-only: the header's "+"
              QuickActionsMenu already offers "Nouvelle tâche" on mobile. */}
          {canCreate && (
          <div className="hidden md:flex">
            <Button
              data-testid="btn-add-task" onClick={() => openCreate()}
              className="gap-2 rounded-r-none"
            >
              <Plus size={16} /> Nouvelle tâche
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="btn-quick-create-task" aria-label="Création rapide par type d'action"
                  className="rounded-l-none border-l border-white/20 px-2"
                >
                  <ChevronDown size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {TASK_TYPES.filter((t) => t.id !== "autre").map((t) => (
                  <DropdownMenuItem key={t.id} onClick={() => openCreate(t.id)} data-testid={`quick-create-${t.id}`}>
                    {t.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          )}
        </div>
      </div>

      <ViewToggle view={view} onChange={setView} />

      <OperationalViewsRow
        counts={counts}
        responsableFilter={responsableFilter} setResponsableFilter={setResponsableFilter}
        dueDateFilter={dueDateFilter} setDueDateFilter={setDueDateFilter}
        statusFilter={statusFilter} setStatusFilter={setStatusFilter}
        priorityFilter={priorityFilter} setPriorityFilter={setPriorityFilter}
      />

      {/* Compact filter bar: search/domaine/priorité stay always visible
          (the three most-used dimensions), everything else lives in the
          "Filtres" popover so this never grows into a multi-row panel.
          Statut is handled only by the Tabs below, never a second select. */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[160px]">
            <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une tâche…" className="pl-8 h-9" data-testid="filter-search"
            />
          </div>
          {/* Domaine/Priorité are always-visible selects on desktop; below md
              they move into the "Filtres" popover (same state) so this bar never
              grows into several rows on a phone. */}
          <div className="hidden md:block">
            <Select value={domainFilter} onValueChange={setDomainFilter}>
              <SelectTrigger className="h-9 w-[9.5rem]" data-testid="filter-domain"><SelectValue placeholder="Domaine" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous domaines</SelectItem>
                {TASK_DOMAINS.map((d) => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="hidden md:block">
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="h-9 w-[9.5rem]" data-testid="filter-priority"><SelectValue placeholder="Priorité" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes priorités</SelectItem>
                {PRIORITY_LEVELS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-1.5" data-testid="btn-filters-toggle">
                <SlidersHorizontal size={14} />
                Filtres{secondaryFilterCount > 0 ? ` ${secondaryFilterCount}` : ""}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 space-y-3">
              {view === "kanban" && (
                <FilterField label="Trier par">
                  <Select value={kanbanSort} onValueChange={setKanbanSort}>
                    <SelectTrigger data-testid="kanban-sort"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {KANBAN_SORTS.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FilterField>
              )}
              {/* Mobile-only: Domaine/Priorité live here instead of the
                  always-visible selects desktop keeps in the bar above (same
                  domainFilter/priorityFilter state, not a second filter). */}
              <div className="md:hidden space-y-3">
                <FilterField label="Domaine">
                  <Select value={domainFilter} onValueChange={setDomainFilter}>
                    <SelectTrigger data-testid="filter-domain-mobile"><SelectValue placeholder="Domaine" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous domaines</SelectItem>
                      {TASK_DOMAINS.map((d) => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FilterField>
                <FilterField label="Priorité">
                  <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                    <SelectTrigger data-testid="filter-priority-mobile"><SelectValue placeholder="Priorité" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes priorités</SelectItem>
                      {PRIORITY_LEVELS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FilterField>
              </div>
              <FilterField label="Type d'action">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    {TASK_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FilterField>
              {/* Without tasks:scope_all, GET /tasks only returns this user's own
                  tasks, so a picker for *other* responsables would have nothing to
                  offer: it stays scope_all-only. "Mes tâches" (operational view
                  above) covers the "just mine" case for everyone. */}
              {canScopeAll && (
                <FilterField label="Responsable">
                  <Select value={responsableFilter} onValueChange={setResponsableFilter}>
                    <SelectTrigger data-testid="filter-responsable"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="moi">Moi</SelectItem>
                      {directory.filter((u) => u.id !== user?.id).map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FilterField>
              )}
              <FilterField label="Prospect">
                <ProspectFilter value={prospectFilter} onChange={setProspectFilter} />
              </FilterField>
              <FilterField label="Échéance">
                <Select value={dueDateFilter} onValueChange={setDueDateFilter}>
                  <SelectTrigger data-testid="filter-due-date"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DUE_DATE_FILTERS.map((d) => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FilterField>
              <Button
                variant="outline" size="sm" className="w-full"
                onClick={resetAdvancedFilters} disabled={!activeFilterChips.length} data-testid="btn-reset-filters"
              >
                Réinitialiser
              </Button>
            </PopoverContent>
          </Popover>
        </div>
        {activeFilterChips.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {activeFilterChips.map((chip) => (
              <button
                key={chip.id} type="button" onClick={chip.onRemove}
                data-testid={`chip-${chip.id}`}
                className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 rounded-full pl-2.5 pr-1.5 py-1 hover:bg-slate-200"
              >
                {chip.label} <X size={12} />
              </button>
            ))}
          </div>
        )}
      </div>

      {view !== "kanban" && (
      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList className="w-full h-auto flex overflow-x-auto justify-start gap-1 md:grid md:grid-cols-6">
          {STATUS_TABS.map((s) => (
            <TabsTrigger key={s.id} value={s.id} data-testid={`tab-${s.id}`} className="shrink-0 md:shrink">
              {s.label} ({s.id === "all" ? counts.all : s.id === "a_faire" ? counts.aFaire
                : s.id === "en_cours" ? counts.enCours : s.id === "en_retard" ? counts.enRetard
                : s.id === "termine" ? counts.termine : counts.annulee})
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      )}

      {/* Flattened on mobile for both views (no border/shadow/rounded/bg):
          List and Timeline render their own bordered per-task cards there,
          and a wrapping Card read as "a card inside a card". */}
      <Card className="overflow-hidden border-0 shadow-none rounded-none bg-transparent md:rounded-xl md:border md:shadow md:bg-card">
        {loading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : view === "kanban" ? (
          <KanbanView
            groups={kanbanGroups} canEdit={canEdit} canDelete={canDelete} canCreate={canCreate} directory={directory}
            resolveAssignee={resolveAssignee} resolveProspectName={resolveProspectName} navigate={navigate}
            onView={openView} onEdit={openEdit} onDelete={setDeletingTask} onAddAssignee={addAssignee}
            onMove={moveTask} onQuickAdd={(columnId) => openCreate(null, columnId)} revertedId={revertedId}
          />
        ) : filtered.length === 0 ? (
          <EmptyState hasActiveFilters={hasActiveFilters} canCreate={canCreate} onCreate={openCreate} onReset={resetFilters} />
        ) : view === "timeline" ? (
          <TaskTimeline
            tasks={filtered} resolveAssignee={resolveAssignee} resolveProspectName={resolveProspectName}
            navigate={navigate} onView={openView} canEdit={canEdit} canDelete={canDelete} directory={directory}
            onToggleDone={toggleDone} onEdit={openEdit} onDelete={setDeletingTask} onAddAssignee={addAssignee}
            todayMarkerRef={todayMarkerRef}
          />
        ) : (
          <ListView
            paged={paged} canEdit={canEdit} canDelete={canDelete} directory={directory} resolveAssignee={resolveAssignee}
            resolveProspectName={resolveProspectName} navigate={navigate} onCycleStatus={cycleStatus} onToggleDone={toggleDone}
            onEdit={openEdit} onDelete={setDeletingTask} onAddAssignee={addAssignee} onView={openView}
            total={filtered.length} pageSize={PAGE_SIZE} currentPage={currentPage} totalPages={totalPages} onPageChange={setPage}
          />
        )}
      </Card>

      <TaskFormSheet
        open={formOpen} onOpenChange={(open) => { setFormOpen(open); if (!open) setCreateStatus(null); }} task={editingTask} canScopeAll={canScopeAll} canEdit={canEdit}
        directory={directory} onSaved={onSaved} onComplete={(task, payload) => setCompleting({ task, payload })} initialType={createType}
      />

      <TaskDetailSheet
        task={viewingTask} onOpenChange={(open) => !open && setViewingTask(null)}
        canEdit={canEdit} canDelete={canDelete} directory={directory} directoryNames={directoryNames}
        resolveAssignee={resolveAssignee} currentUser={user}
        navigate={navigate} onEdit={openEdit} onDelete={setDeletingTask} onMarkDone={markDone}
        onPostpone={postpone} onAddAssignee={addAssignee} onCycleStatus={cycleStatus}
      />

      <CompleteTaskDialog task={completing?.task} user={user} onClose={() => setCompleting(null)} onConfirm={(_, comment) => confirmComplete(comment)} />
      <CancelTaskDialog task={cancelling?.task} onClose={() => setCancelling(null)} onConfirm={confirmCancel} />
      <DeleteTaskDialog task={deletingTask} onOpenChange={(open) => !open && setDeletingTask(null)} onConfirm={confirmDelete} />
    </div>
  );
}
